import { FormEvent, useEffect, useMemo, useState } from 'react';
import { Link, useParams, useSearchParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import { PageHeader } from '../../components/layout/page/PageHeader';
import { PageShell } from '../../components/layout/page/PageShell';
import {
  COMPETENCY_PROFICIENCY_OPTIONS,
  CompetencyProficiency,
  EvaluationIndicatorFeedback,
  EvaluationRating,
  PerformanceEvaluation,
  getPerformanceEvaluation,
  submitManagerEvaluation,
  submitSelfEvaluation,
} from '../../services/peoplePerformanceApi';

type EvaluationMode = 'self' | 'manager';

type RatingDraft = {
  competencyId: string;
  proficiency: CompetencyProficiency | '';
  comment: string;
};

type FeedbackDraft = {
  indicatorId: string;
  feedback: string;
};

type IndicatorGroup = {
  indicatorId: string;
  indicatorName: string;
  audience: string;
  ratings: EvaluationRating[];
  feedback: EvaluationIndicatorFeedback | null;
};

function resolveMode(value: string | null): EvaluationMode {
  return value === 'manager' ? 'manager' : 'self';
}

function currentProficiency(rating: EvaluationRating, mode: EvaluationMode): CompetencyProficiency | '' {
  const raw = mode === 'self' ? rating.selfProficiency : rating.managerProficiency;
  if (!raw) return '';
  const match = COMPETENCY_PROFICIENCY_OPTIONS.find((option) => option.value === raw);
  return match?.value ?? '';
}

function currentComment(rating: EvaluationRating, mode: EvaluationMode): string {
  return mode === 'self' ? rating.selfComment : rating.managerComment;
}

function currentFeedback(feedback: EvaluationIndicatorFeedback | null, mode: EvaluationMode): string {
  if (!feedback) return '';
  return mode === 'self' ? feedback.selfFeedback : feedback.managerFeedback;
}

export function PerformanceEvaluationPage() {
  const { cycleId, evaluationId } = useParams();
  const [searchParams, setSearchParams] = useSearchParams();
  const mode = resolveMode(searchParams.get('mode'));
  const isPerformanceRoute = window.location.pathname.startsWith('/performance/');

  const [evaluation, setEvaluation] = useState<PerformanceEvaluation | null>(null);
  const [ratingDrafts, setRatingDrafts] = useState<Record<string, RatingDraft>>({});
  const [feedbackDrafts, setFeedbackDrafts] = useState<Record<string, FeedbackDraft>>({});
  const [overallComment, setOverallComment] = useState('');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const isSubmitted =
    mode === 'self'
      ? Boolean(evaluation?.selfSubmittedAtUtc)
      : Boolean(evaluation?.managerSubmittedAtUtc);
  const canWrite = !isSubmitted;

  const groups = useMemo((): IndicatorGroup[] => {
    if (!evaluation) return [];

    const feedbackByIndicator = new Map(
      evaluation.indicatorFeedbacks.map((item) => [item.indicatorId, item]),
    );
    const byIndicator = new Map<string, IndicatorGroup>();

    for (const rating of evaluation.ratings) {
      const existing = byIndicator.get(rating.indicatorId);
      if (existing) {
        existing.ratings.push(rating);
        continue;
      }

      const feedback = feedbackByIndicator.get(rating.indicatorId) ?? null;
      byIndicator.set(rating.indicatorId, {
        indicatorId: rating.indicatorId,
        indicatorName: feedback?.indicatorName || 'Indicador',
        audience: feedback?.audience || '',
        ratings: [rating],
        feedback,
      });
    }

    for (const feedback of evaluation.indicatorFeedbacks) {
      if (!byIndicator.has(feedback.indicatorId)) {
        byIndicator.set(feedback.indicatorId, {
          indicatorId: feedback.indicatorId,
          indicatorName: feedback.indicatorName,
          audience: feedback.audience,
          ratings: [],
          feedback,
        });
      }
    }

    return Array.from(byIndicator.values()).sort((a, b) => a.indicatorName.localeCompare(b.indicatorName));
  }, [evaluation]);

  async function loadData(): Promise<void> {
    if (!cycleId || !evaluationId) return;
    setLoading(true);
    try {
      const result = await getPerformanceEvaluation(cycleId, evaluationId);
      setEvaluation(result);
      hydrateDrafts(result, mode);
    } catch {
      toast.error('Falha ao carregar avaliação.');
      setEvaluation(null);
    } finally {
      setLoading(false);
    }
  }

  function hydrateDrafts(result: PerformanceEvaluation, nextMode: EvaluationMode): void {
    const nextRatings: Record<string, RatingDraft> = {};
    for (const rating of result.ratings) {
      nextRatings[rating.competencyId] = {
        competencyId: rating.competencyId,
        proficiency: currentProficiency(rating, nextMode),
        comment: currentComment(rating, nextMode),
      };
    }
    setRatingDrafts(nextRatings);

    const nextFeedbacks: Record<string, FeedbackDraft> = {};
    for (const feedback of result.indicatorFeedbacks) {
      nextFeedbacks[feedback.indicatorId] = {
        indicatorId: feedback.indicatorId,
        feedback: currentFeedback(feedback, nextMode),
      };
    }
    setFeedbackDrafts(nextFeedbacks);
    setOverallComment(
      nextMode === 'self' ? result.selfOverallComment : result.managerOverallComment,
    );
  }

  useEffect(() => {
    void loadData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cycleId, evaluationId]);

  useEffect(() => {
    if (evaluation) {
      hydrateDrafts(evaluation, mode);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mode]);

  function setMode(next: EvaluationMode): void {
    setSearchParams(next === 'manager' ? { mode: 'manager' } : {});
  }

  function updateRating(
    competencyId: string,
    patch: Partial<Pick<RatingDraft, 'proficiency' | 'comment'>>,
  ): void {
    setRatingDrafts((current) => {
      const existing = current[competencyId];
      if (!existing) return current;
      return { ...current, [competencyId]: { ...existing, ...patch } };
    });
  }

  function updateFeedback(indicatorId: string, feedback: string): void {
    setFeedbackDrafts((current) => {
      const existing = current[indicatorId] ?? { indicatorId, feedback: '' };
      return { ...current, [indicatorId]: { ...existing, feedback } };
    });
  }

  async function handleSubmit(event: FormEvent): Promise<void> {
    event.preventDefault();
    if (!cycleId || !evaluationId || !canWrite) return;

    const missing = Object.values(ratingDrafts).some((item) => !item.proficiency);
    if (missing) {
      toast.error('Informe a nota de todas as competências.');
      return;
    }

    setSaving(true);
    try {
      const payload = {
        overallComment,
        ratings: Object.values(ratingDrafts).map((item) =>
          mode === 'self'
            ? {
                competencyId: item.competencyId,
                selfProficiency: item.proficiency || null,
                selfComment: item.comment,
              }
            : {
                competencyId: item.competencyId,
                managerProficiency: item.proficiency || null,
                managerComment: item.comment,
              },
        ),
        indicatorFeedbacks: Object.values(feedbackDrafts).map((item) =>
          mode === 'self'
            ? { indicatorId: item.indicatorId, selfFeedback: item.feedback }
            : { indicatorId: item.indicatorId, managerFeedback: item.feedback },
        ),
      };

      const result =
        mode === 'self'
          ? await submitSelfEvaluation(cycleId, evaluationId, payload)
          : await submitManagerEvaluation(cycleId, evaluationId, payload);

      setEvaluation(result);
      hydrateDrafts(result, mode);
      toast.success(mode === 'self' ? 'Autoavaliação enviada.' : 'Avaliação do gestor enviada.');
    } catch {
      toast.error('Falha ao enviar avaliação.');
    } finally {
      setSaving(false);
    }
  }

  if (loading) {
    return (
      <PageShell>
        <p className="text-secondary-500">Carregando...</p>
      </PageShell>
    );
  }

  if (!evaluation || !cycleId) {
    return (
      <PageShell>
        <p className="text-secondary-500">Avaliação não encontrada.</p>
      </PageShell>
    );
  }

  return (
    <PageShell>
      <PageHeader
        title={evaluation.subjectUserName || 'Avaliação de desempenho'}
        description="Notas por competência (1–5) e feedback opcional por indicador."
        actions={(
          <Link
            to={isPerformanceRoute ? '/performance' : `/people/evaluation-cycles/${cycleId}`}
            className="erp-btn-secondary text-sm"
          >
            {isPerformanceRoute ? 'Voltar ao desempenho' : 'Voltar ao ciclo'}
          </Link>
        )}
      />

      <div className="mb-4 flex flex-wrap items-center gap-2">
        <button
          type="button"
          className={mode === 'self' ? 'erp-btn-primary text-sm' : 'erp-btn-secondary text-sm'}
          onClick={() => setMode('self')}
        >
          Autoavaliação
        </button>
        <button
          type="button"
          className={mode === 'manager' ? 'erp-btn-primary text-sm' : 'erp-btn-secondary text-sm'}
          onClick={() => setMode('manager')}
        >
          Avaliação do gestor
        </button>
        <span className="text-sm text-secondary-500">
          Gestor: {evaluation.managerUserName || '—'} · Status: {evaluation.status}
          {isSubmitted ? ' · Enviada (somente leitura)' : ' · Em edição'}
        </span>
      </div>

      <form onSubmit={(event) => void handleSubmit(event)} className="space-y-4">
        {isSubmitted ? (
          <p className="rounded-xl border border-secondary-200 bg-secondary-50 px-4 py-3 text-sm text-secondary-600">
            Esta {mode === 'self' ? 'autoavaliação' : 'avaliação do gestor'} já foi enviada e não pode mais ser editada.
          </p>
        ) : null}
        {groups.length === 0 ? (
          <p className="rounded-xl border border-secondary-200 bg-white p-4 text-sm text-secondary-500">
            Nenhuma competência vinculada a esta avaliação. Confira os indicadores do ciclo.
          </p>
        ) : (
          groups.map((group) => (
            <section key={group.indicatorId} className="rounded-xl border border-secondary-200 bg-white p-4">
              <div className="mb-3">
                <h2 className="text-lg font-semibold text-secondary-900">{group.indicatorName}</h2>
                {group.audience ? (
                  <p className="text-sm text-secondary-500">Público: {group.audience}</p>
                ) : null}
              </div>

              <div className="space-y-3">
                {group.ratings.map((rating) => {
                  const draft = ratingDrafts[rating.competencyId];
                  return (
                    <div
                      key={rating.competencyId}
                      className="grid gap-3 border-t border-secondary-100 pt-3 md:grid-cols-[1fr_220px]"
                    >
                      <div>
                        <p className="font-medium text-secondary-900">
                          {rating.competencyName}
                        </p>
                        <label className="erp-label mt-2 block">
                          Comentário (opcional)
                          <textarea
                            className="erp-input mt-1"
                            rows={2}
                            disabled={!canWrite}
                            value={draft?.comment ?? ''}
                            onChange={(event) =>
                              updateRating(rating.competencyId, { comment: event.target.value })
                            }
                          />
                        </label>
                      </div>
                      <label className="erp-label">
                        Nota
                        <select
                          className="erp-input mt-1"
                          required
                          disabled={!canWrite}
                          value={draft?.proficiency ?? ''}
                          onChange={(event) =>
                            updateRating(rating.competencyId, {
                              proficiency: event.target.value as CompetencyProficiency | '',
                            })
                          }
                        >
                          <option value="">Selecione...</option>
                          {COMPETENCY_PROFICIENCY_OPTIONS.map((option) => (
                            <option key={option.value} value={option.value}>
                              {option.score} — {option.label}
                            </option>
                          ))}
                        </select>
                      </label>
                    </div>
                  );
                })}
              </div>

              <label className="erp-label mt-4 block">
                Feedback do indicador (opcional)
                <textarea
                  className="erp-input mt-1"
                  rows={3}
                  disabled={!canWrite}
                  placeholder="Complemento da nota para este indicador..."
                  value={feedbackDrafts[group.indicatorId]?.feedback ?? ''}
                  onChange={(event) => updateFeedback(group.indicatorId, event.target.value)}
                />
              </label>
            </section>
          ))
        )}

        <section className="rounded-xl border border-secondary-200 bg-white p-4">
          <label className="erp-label block">
            Comentário geral (opcional)
            <textarea
              className="erp-input mt-1"
              rows={3}
              disabled={!canWrite}
              value={overallComment}
              onChange={(event) => setOverallComment(event.target.value)}
            />
          </label>
        </section>

        {canWrite ? (
          <div className="flex justify-end">
            <button type="submit" className="erp-btn-primary" disabled={saving || groups.length === 0}>
              {saving ? 'Enviando...' : mode === 'self' ? 'Enviar autoavaliação' : 'Enviar avaliação do gestor'}
            </button>
          </div>
        ) : null}
      </form>
    </PageShell>
  );
}
